import * as Phaser from 'phaser';
import { ProceduralSpriteFactory, UnitSprite } from '@/factories/ProceduralSpriteFactory';
import { GAME } from '@/config/GameConfig';
import { world } from '@/net/PlayroomBridge';

/**
 * BattleFront — 5 strategic defense lanes. Humans push from the bottom,
 * Void seeds spawn at the top and advance. Click a Void seed to fire a
 * Neural Spike and flip it to your team.
 *
 * This is the initial skeleton: spawn + march + takeover loop. Damage /
 * resource costs land in the next iteration.
 */
export class BattleFrontScene extends Phaser.Scene {
  private factory!: ProceduralSpriteFactory;
  private units: UnitSprite[] = [];
  private spawnTimer!: Phaser.Time.TimerEvent;

  constructor() {
    super('BattleFrontScene');
  }

  create(): void {
    const { width, height } = this.scale;
    this.factory = new ProceduralSpriteFactory(this);
    this.cameras.main.setBackgroundColor('#050505');

    drawLanes(this, width, height);
    drawFrontline(this, width, height);

    // Label.
    const title = this.add
      .text(width / 2, 60, 'BATTLE FRONT // CHALLENGE ' + world.challengeId().slice(0, 8), {
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: '14px',
        color: '#ffffff',
      })
      .setOrigin(0.5);
    title.setLetterSpacing?.(6);

    // Friendly line — one starship per lane at the bottom.
    for (let lane = 0; lane < GAME.LANE_COUNT; lane++) {
      const x = laneX(width, lane);
      const ship = this.factory.starship(x, height - 220, 'friendly');
      this.units.push(ship);
    }

    // Periodic void seed spawner.
    this.spawnTimer = this.time.addEvent({
      delay: 2200,
      loop: true,
      callback: () => this.spawnVoidSeed(),
    });

    this.input.on('gameobjectdown', (_p: unknown, obj: Phaser.GameObjects.GameObject) => {
      const unit = this.units.find((u) => u.container === obj);
      if (unit && unit.team === 'hostile') {
        const now = unit.neuralSpike();
        if (now === 'friendly') {
          // Bump the shared void index down — small reward for the takeover.
          world.setVoidIndex(Math.max(0, world.getVoidIndex() - 0.02));
        }
      }
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.spawnTimer?.remove(false);
      this.units = [];
    });
  }

  private spawnVoidSeed(): void {
    const { width } = this.scale;
    const lane = Math.floor(Math.random() * GAME.LANE_COUNT);
    const x = laneX(width, lane);
    const seed = this.factory.voidSeed(x, -40);

    // March down the lane.
    const marchTween = this.tweens.add({
      targets: seed.container,
      y: this.scale.height + 60,
      duration: 14_000,
      ease: 'Linear',
      onComplete: () => {
        // Breached — nudges void index up.
        world.setVoidIndex(Math.min(1, world.getVoidIndex() + 0.04));
        seed.container.destroy();
        this.units = this.units.filter((u) => u !== seed);
      },
    });

    // Make interactive for Neural Spike.
    seed.container.setInteractive(
      new Phaser.Geom.Rectangle(-32, -32, 64, 64),
      Phaser.Geom.Rectangle.Contains,
    );
    seed.container.on('pointerdown', () => {
      if (seed.team === 'hostile') {
        seed.neuralSpike();
        // Flipped seeds stop marching and drift back upward.
        marchTween.stop();
        this.tweens.add({
          targets: seed.container,
          y: seed.container.y - 80,
          alpha: 0,
          duration: 900,
          onComplete: () => {
            seed.container.destroy();
            this.units = this.units.filter((u) => u !== seed);
          },
        });
      }
    });

    this.units.push(seed);
  }
}

function laneX(width: number, lane: number): number {
  const margin = width * 0.1;
  const span = width - margin * 2;
  return margin + (span / (GAME.LANE_COUNT - 1)) * lane;
}

function drawLanes(scene: Phaser.Scene, w: number, h: number): void {
  const g = scene.add.graphics();
  g.lineStyle(1, 0xffffff, 0.08);
  for (let i = 0; i < GAME.LANE_COUNT; i++) {
    const x = laneX(w, i);
    g.beginPath();
    g.moveTo(x, 80);
    g.lineTo(x, h - 100);
    g.strokePath();
  }
  // Lane labels.
  for (let i = 0; i < GAME.LANE_COUNT; i++) {
    const x = laneX(w, i);
    scene.add
      .text(x, h - 80, `L${i + 1}`, {
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: '12px',
        color: '#55555c',
      })
      .setOrigin(0.5);
  }
}

function drawFrontline(scene: Phaser.Scene, w: number, h: number): void {
  const g = scene.add.graphics();
  // Top void band.
  g.fillStyle(0xff2040, 0.06);
  g.fillRect(0, 0, w, 80);
  g.lineStyle(1, 0xff2040, 0.5);
  g.beginPath();
  g.moveTo(0, 80);
  g.lineTo(w, 80);
  g.strokePath();
  // Bottom friendly base line.
  g.fillStyle(0x1d6bff, 0.05);
  g.fillRect(0, h - 100, w, 100);
  g.lineStyle(1, 0x1d6bff, 0.5);
  g.beginPath();
  g.moveTo(0, h - 100);
  g.lineTo(w, h - 100);
  g.strokePath();
}
